import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { assemblyBox, DEFAULT_PIN, makeDivider, splitFront, splitShelvesByPanel, defaultPanel, EDGE_IDS, findOverlap, frontOfPart, frontShape, frontSpaces, HINGE_LABEL, makeFront, type FrontSpace, makeShelfSet, shelfBoundaries, shelfOfPart, type ShelfBoundaries, type ShelfSet, flipPanel, swapOverlapInDoc, formatMm, nextPanelKey, panelPropertyOnAxis, partBox, type Pin, type Vec3, evaluateProject, migrateDocument, mm, type AssemblyInstance, type CabinetInstance, type EdgeId, type ProjectDocument } from '@scs/domain-engine';
import type { PresetSummaryDto } from '@scs/schemas';
import type { ProjectSummaryDto } from '@scs/schemas';
import { Api, ApiError } from '../lib/api';
import { readJournal, clearJournal, type JournalEntry } from '../lib/journal';
import { navigate } from '../lib/router';
import { t } from '../lib/i18n';
import { Logo } from '../lib/ui';
import { TopbarUser, useSession } from '../App';
import { EditorStore, type SaveState, type Command } from './store';
import { ThreeViewport, type MoveTarget, type ViewName, type ViewOptions, type EdgeRef } from './viewport';
import { applyConnectedMoves, connectedMoves, pendingResize } from './connected';
import { LinkDialog, linkCandidates, linkEqual, linkLines, LinksSection, SHELF_PROPS } from './Links';
import { partWorldBox, roomPlanes, snapMove, unitWorldBox, worldDeltaToLocal, type SnapTarget, type WBox } from './snap';
import { panelKeyOfPart } from './AssemblyProperties';
import { CabinetProperties, PartProperties, ProjectProperties } from './Properties';
import { BottomPanel } from './BottomPanel';
import { LibraryPanel, usePresets } from './LibraryPanel';
import { PresetSection } from './PresetSection';
import { AssemblyProperties } from './AssemblyProperties';
import { SceneTree } from './SceneTree';
import { ShelfProperties } from './ShelfProperties';
import { FrontGroupProperties, FrontProperties, HINGES } from './FrontProperties';
import { PresetWizard } from './PresetWizard';
import { instantiatePreset, loadVersion, newUnitId } from './presets';
import { CatalogContext, decorViews, useCatalog } from '../lib/catalog';

/** All placed units of a document (mutable references inside an exec draft). */
function allUnits(d: ProjectDocument): (CabinetInstance | AssemblyInstance)[] {
  return [...d.cabinets, ...d.assemblies];
}

interface Loaded {
  project: ProjectSummaryDto;
  role: string;
  revision: number;
  doc: ProjectDocument;
  journal: JournalEntry | null;
}

export function EditorPage({ projectId }: { projectId: string }) {
  const { me } = useSession();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await Api.project(projectId);
        const doc = migrateDocument(p.document);
        const j = (await readJournal(me.user.id, projectId)) ?? null;
        if (!cancelled) setLoaded({ project: p.project, role: p.role, revision: p.revision, doc, journal: j && JSON.stringify(j.document) !== JSON.stringify(doc) ? j : null });
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError && e.status === 404 ? 'Projekat ne postoji ili nemate pristup.' : (e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, me.user.id]);

  if (error)
    return (
      <div className="auth-page">
        <div className="card auth-card">
          <p className="error-text">{error}</p>
          <button className="btn" onClick={() => navigate('/')}>
            {t('editor.backToDash')}
          </button>
        </div>
      </div>
    );
  if (!loaded) return <div className="auth-page muted">…</div>;
  return <Editor loaded={loaded} userId={me.user.id} />;
}

const SAVE_LABEL: Record<SaveState, Parameters<typeof t>[0]> = {
  saved: 'save.saved',
  dirty: 'save.dirty',
  saving: 'save.saving',
  offline: 'save.offline',
  conflict: 'save.conflict',
  error: 'save.error',
  expired: 'save.expired',
  readonly: 'save.readonly',
};

function Editor({ loaded, userId }: { loaded: Loaded; userId: string }) {
  const readOnly = loaded.role === 'viewer';
  const store = useMemo(() => new EditorStore(loaded.project.id, userId, loaded.doc, loaded.revision, readOnly), [loaded, userId, readOnly]);
  useEffect(() => store.attach(), [store]);
  const state = useSyncExternalStore(store.subscribe, store.getState);
  const { doc, selection } = state;
  const ev = useMemo(() => evaluateProject(doc), [doc]);
  /** Gizmo / point-snap subject: a panel of an assembly if one is selected, else the single selected unit. */
  const moveTarget = useMemo((): MoveTarget | null => {
    if (selection.cabinetIds.length !== 1 || (selection.extraPartIds ?? []).length) return null;
    const unitId = selection.cabinetIds[0]!;
    const asm = doc.assemblies.find((a) => a.id === unitId);
    if (asm?.locked || doc.cabinets.find((c) => c.id === unitId)?.locked) return null;
    if (asm && selection.partId?.startsWith(`${unitId}/`)) {
      const lk = selection.partId.split('/')[1]!;
      if (shelfOfPart(asm, lk) || frontOfPart(asm, lk)) return null;
      return { kind: 'panel', unitId, partId: selection.partId };
    }
    return { kind: 'unit', unitId };
  }, [selection, doc]);
  const [journal, setJournal] = useState<JournalEntry | null>(loaded.journal);
  const [opts, setOpts] = useState<ViewOptions>({ grid: true, xray: false, exploded: false, labels: false, edges: false, openFronts: false, links: true });
  const [gizmoOn, setGizmoOn] = useState(true);
  const [snapInfo, setSnapInfo] = useState('');
  const [cornerState, setCornerState] = useState<'idle' | 'selected'>('idle');
  const [pinState, setPinState] = useState<'idle' | 'moving'>('idle');
  const [dupDrag, setDupDrag] = useState(false);
  const [edgePick, setEdgePick] = useState<{ source: EdgeRef | null; target: EdgeRef | null }>({ source: null, target: null });
  const [linkOpen, setLinkOpen] = useState(false);
  /** The last gizmo move command (for typing the exact distance afterwards). */
  const lastMove = useRef<Command | null>(null);
  /** Percent typed per axis in the centre card. */
  const [centerPct, setCenterPct] = useState<Record<number, string>>({});
  /** "Izaberi prostor" for new shelves when the boundaries enclose several compartments. */
  const [regionPick, setRegionPick] = useState<{ unitId: string; b: ShelfBoundaries } | null>(null);
  /** "+ Fronta": openings of the assembly's front view; Ctrl+click joins several into one front. */
  const [frontPick, setFrontPick] = useState<{ unitId: string; spaces: FrontSpace[]; selected: number[]; betweenShelves: boolean } | null>(null);
  /** "+ Pregrada": pick one opening, then the direction of the cut. */
  const [dividerPick, setDividerPick] = useState<{ unitId: string; spaces: FrontSpace[]; selected: number | null; betweenShelves: boolean } | null>(null);
  /** Inset fronts colliding with shelves: the user answered "Ne" for this state of the document. */
  const [shelfFitDismissed, setShelfFitDismissed] = useState<ProjectDocument | null>(null);
  // "Update connected": offered while the last commands resized panels and one of them is still selected.
  // Deselecting dismisses it for this document state (it comes back only with a new resize).
  const dismissedDoc = useRef<ProjectDocument | null>(null);
  const connected = useMemo(() => {
    if (readOnly || dismissedDoc.current === doc) return null;
    const pending = pendingResize(state.past, doc);
    if (!pending) return null;
    const selParts = [selection.partId, ...(selection.extraPartIds ?? [])].filter((x): x is string => !!x);
    const isSelected = pending.changed.some((c) => {
      const asm = doc.assemblies.find((a) => a.id === c.unitId);
      if (!asm) return false;
      if (selParts.length) return selParts.some((pid) => pid.startsWith(`${c.unitId}/`) && panelKeyOfPart(asm, pid.split('/')[1]!) === c.panelKey);
      return selection.cabinetIds.includes(c.unitId);
    });
    if (!isSelected) {
      dismissedDoc.current = doc;
      return null;
    }
    const moves = connectedMoves(pending.base, doc, pending.changed, undefined, ev);
    return moves.length ? moves : null;
  }, [state.past, doc, ev, selection, readOnly]);
  // "Copy dimension": length of the source edge → the target panel's dimension along the target edge.
  const copyDim = useMemo(() => {
    const { source, target } = edgePick;
    if (!source || !target || readOnly) return null;
    const partOf = (id: string) => {
      const unit = ev.cabinets.find((c) => c.instance.id === id.split('/')[0]);
      return unit && { unit, part: unit.result.parts.find((p) => p.id === id) };
    };
    const src = partOf(source.partId);
    const dst = partOf(target.partId);
    if (!src?.part || !dst?.part) return null;
    const value = partBox(src.part).size[source.axis];
    const current = partBox(dst.part).size[target.axis];
    const asm = doc.assemblies.find((a) => a.id === dst.unit.instance.id);
    const key = asm && panelKeyOfPart(asm, dst.part.localKey);
    const panel = key ? asm!.panels.find((p) => p.key === key) : undefined;
    const prop = panel && panelPropertyOnAxis(panel, target.axis);
    const PROP = { length: 'dužina', width: 'širina', thickness: 'debljina' } as const;
    return {
      value,
      current,
      label: dst.part.label,
      prop: prop ? PROP[prop] : null,
      blocked: !panel ? 'Ciljna ploča je dio parametarskog korpusa — mjera se mijenja u njegovim parametrima.' : asm!.locked ? 'Ciljni sklop je zaključan.' : null,
      apply: () => {
        if (!asm || !key || !prop) return;
        store.exec('Kopiranje dimenzije', (d) => {
          const p = d.assemblies.find((a) => a.id === asm.id)?.panels.find((x) => x.key === key);
          if (p) p[prop] = value;
        });
        vpRef.current?.clearEdges();
      },
    };
  }, [edgePick, ev, doc, readOnly, store]);
  const linkItems = useMemo(() => (readOnly ? [] : linkCandidates(doc, selection)), [doc, selection, readOnly]);
  // Two horizontal panels of one assembly (picked with Ctrl+click): shelves can go between them.
  const shelfCandidate = useMemo(() => {
    const [a, b] = linkItems;
    if (readOnly || linkItems.length !== 2 || a!.kind !== 'panel' || b!.kind !== 'panel' || a!.unitId !== b!.unitId) return null;
    const asm = doc.assemblies.find((x) => x.id === a!.unitId);
    if (!asm || asm.locked) return null;
    const r = shelfBoundaries(asm, a!.panelKey!, b!.panelKey!);
    return 'error' in r ? { error: r.error } : { unitId: asm.id, b: r };
  }, [linkItems, doc, readOnly]);
  const createShelves = (unitId: string, b: ShelfBoundaries, index: number) => {
    let id = '';
    store.exec('Nove police', (d) => {
      const a = d.assemblies.find((x) => x.id === unitId)!;
      const set = makeShelfSet(a, b, b.compartments[index]!);
      id = set.id;
      a.shelfSets = [...(a.shelfSets ?? []), set];
    });
    if (id) store.select({ cabinetIds: [unitId], partId: null, shelfSetId: id });
  };
  const startShelves = () => {
    if (!shelfCandidate || 'error' in shelfCandidate) return;
    const { unitId, b } = shelfCandidate;
    if (b.compartments.length === 1) return createShelves(unitId, b, 0);
    setRegionPick({ unitId, b });
    vpRef.current?.setRegionPick(unitId, b.compartments);
  };
  const cancelRegionPick = () => {
    setRegionPick(null);
    vpRef.current?.setRegionPick(null);
  };
  const frontUnit = selection.cabinetIds.length === 1 ? doc.assemblies.find((a) => a.id === selection.cabinetIds[0]) : undefined;
  const startFront = (betweenShelves = false) => {
    if (!frontUnit || frontUnit.locked) return;
    cancelRegionPick();
    setDividerPick(null);
    const spaces = frontSpaces(frontUnit, { betweenShelves });
    const zf = assemblyBox(frontUnit).max[2];
    setFrontPick({ unitId: frontUnit.id, spaces, selected: [], betweenShelves });
    vpRef.current?.setRegionPick(
      frontUnit.id,
      spaces.map((sp) => ({ min: [sp.rect.x0, sp.rect.y0, zf - mm(12)] as Vec3, max: [sp.rect.x1, sp.rect.y1, zf + mm(4)] as Vec3 })),
    );
  };
  const cancelFrontPick = () => {
    setFrontPick(null);
    vpRef.current?.setRegionPick(null);
  };
  const startDivider = (betweenShelves = false) => {
    if (!frontUnit || frontUnit.locked) return;
    cancelRegionPick();
    setFrontPick(null);
    const spaces = frontSpaces(frontUnit, { betweenShelves });
    const zf = assemblyBox(frontUnit).max[2];
    setDividerPick({ unitId: frontUnit.id, spaces, selected: null, betweenShelves });
    vpRef.current?.setRegionPick(
      frontUnit.id,
      spaces.map((sp) => ({ min: [sp.rect.x0, sp.rect.y0, zf - mm(12)] as Vec3, max: [sp.rect.x1, sp.rect.y1, zf + mm(4)] as Vec3 })),
    );
  };
  const cancelDivider = () => {
    setDividerPick(null);
    vpRef.current?.setRegionPick(null);
  };
  /** Splits the selected front in two; the original stays selected as the left / bottom part. */
  const doSplitFront = (unitId: string, frontId: string, direction: 'vertical' | 'horizontal') =>
    store.exec(direction === 'vertical' ? 'Podjela fronte lijevo/desno' : 'Podjela fronte gore/dolje', (d) => {
      const a = d.assemblies.find((x) => x.id === unitId);
      if (a && !a.locked) splitFront(a, frontId, direction);
    });
  const createDivider = (direction: 'vertical' | 'horizontal') => {
    if (!dividerPick || dividerPick.selected === null) return;
    const { unitId, spaces, selected } = dividerPick;
    let key = '';
    store.exec(direction === 'vertical' ? 'Nova pregrada' : 'Nova fiksna polica', (d) => {
      const a = d.assemblies.find((x) => x.id === unitId)!;
      const panel = makeDivider(a, spaces[selected]!, direction);
      key = panel.key;
      a.panels.push(panel);
      // Shelves in the cut space follow: split left/right, or the part above gets its own set.
      splitShelvesByPanel(a, panel);
    });
    cancelDivider();
    // The new panel stays selected (gizmo / centring) — afterwards it is an ordinary panel.
    if (key) store.select({ cabinetIds: [unitId], partId: `${unitId}/${key}` });
  };
  // Inset fronts of the selected assembly that run into shelves: offer to shorten those shelves.
  const shelfFit = useMemo(() => {
    if (readOnly || !frontUnit || shelfFitDismissed === doc) return null;
    const unit = ev.cabinets.find((c) => c.instance.id === frontUnit.id);
    if (!unit) return null;
    const hits: { setId: string; index: number; cut: number }[] = [];
    for (const f of frontUnit.fronts ?? []) {
      if (f.mode !== 'inset') continue;
      const fp = unit.result.parts.find((p) => p.localKey === f.id);
      if (!fp) continue;
      const fb = partBox(fp);
      for (const p of unit.result.parts) {
        const sh = shelfOfPart(frontUnit, p.localKey);
        if (!sh) continue;
        const b = partBox(p);
        const ov = [0, 1, 2].map((k) => Math.min(b.min[k]! + b.size[k]!, fb.min[k]! + fb.size[k]!) - Math.max(b.min[k]!, fb.min[k]!));
        if (ov.every((o) => o > 500)) hits.push({ setId: sh.set.id, index: sh.index, cut: b.min[2] + b.size[2] - fb.min[2] });
      }
    }
    return hits.length ? { unitId: frontUnit.id, hits } : null;
  }, [frontUnit, ev, doc, readOnly, shelfFitDismissed]);
  const fitShelves = () => {
    if (!shelfFit) return;
    store.exec('Prilagođavanje polica fronti', (d) => {
      const a = d.assemblies.find((x) => x.id === shelfFit.unitId);
      for (const h of shelfFit.hits) {
        const set = a?.shelfSets?.find((x) => x.id === h.setId);
        if (!set) continue;
        set.setbacks = { ...(set.setbacks ?? {}), [String(h.index)]: (set.setbacks?.[String(h.index)] ?? 0) + h.cut };
      }
    });
  };

  const frontPreview = useMemo(() => {
    if (!frontPick || !frontPick.selected.length) return null;
    const asm = doc.assemblies.find((a) => a.id === frontPick.unitId);
    return asm ? frontShape(asm, { spaces: frontPick.selected.map((i) => frontPick.spaces[i]!.ref), mode: 'overlay', thickness: mm(18) }) : null;
  }, [frontPick, doc]);
  const createFront = () => {
    if (!frontPick || !frontPick.selected.length) return;
    const { unitId, spaces, selected } = frontPick;
    let id = '';
    store.exec('Nova fronta', (d) => {
      const a = d.assemblies.find((x) => x.id === unitId)!;
      const f = makeFront(a, selected.map((i) => spaces[i]!));
      id = f.id;
      a.fronts = [...(a.fronts ?? []), f];
    });
    cancelFrontPick();
    if (id) store.select({ cabinetIds: [unitId], partId: `${unitId}/${id}` });
  };

  useEffect(() => {
    if (!vpRef.current) return;
    vpRef.current.onRegionPick = (i, additive) => {
      if (dividerPick) {
        setDividerPick({ ...dividerPick, selected: i });
        vpRef.current?.setRegionSelected([i]);
        return;
      }
      if (frontPick) {
        const selected = additive ? (frontPick.selected.includes(i) ? frontPick.selected.filter((x) => x !== i) : [...frontPick.selected, i]) : [i];
        setFrontPick({ ...frontPick, selected });
        vpRef.current?.setRegionSelected(selected);
        return;
      }
      if (!regionPick) return;
      cancelRegionPick();
      createShelves(regionPick.unitId, regionPick.b, i);
    };
  });
  const shelfItems = linkItems.length >= 2 && linkItems.every((i) => i.kind === 'shelves') ? linkItems : null;

  // Centre between neighbours: per axis, the nearest panels/units on both sides that face the moved one.
  const centerOptions = useMemo(() => {
    if (readOnly || !moveTarget) return [];
    const unit = ev.cabinets.find((c) => c.instance.id === moveTarget.unitId);
    if (!unit) return [];
    const part = moveTarget.kind === 'panel' ? unit.result.parts.find((p) => p.id === moveTarget.partId) : undefined;
    const me = part ? partWorldBox(unit, part) : unitWorldBox(unit);
    const others: WBox[] = [];
    for (const c of ev.cabinets) {
      if (c.instance.hidden) continue;
      if (moveTarget.kind === 'panel') {
        for (const p of c.result.parts) if (p.id !== moveTarget.partId) others.push(partWorldBox(c, p));
      } else if (c.instance.id !== unit.instance.id) others.push(unitWorldBox(c));
    }
    const out: { axis: 0 | 1 | 2; lo: number; hi: number; pct: number; label: string }[] = [];
    for (const k of [0, 1, 2] as const) {
      // A neighbour must face at least half of the moved object (thin parts touching it, such as
      // shelves resting against a divider, are not the space it is centred in).
      const overlaps = (b: WBox) => [0, 1, 2].every((j) => j === k || Math.min(b.max[j]!, me.max[j]!) - Math.max(b.min[j]!, me.min[j]!) >= Math.max(500, (me.max[j]! - me.min[j]!) / 2));
      let lo = -Infinity;
      let hi = Infinity;
      for (const b of others) {
        if (!overlaps(b)) continue;
        if (b.max[k]! <= me.min[k]! + 500) lo = Math.max(lo, b.max[k]!);
        if (b.min[k]! >= me.max[k]! - 500) hi = Math.min(hi, b.min[k]!);
      }
      if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi - lo <= 0) continue;
      const centre = (me.min[k]! + me.max[k]!) / 2;
      out.push({ axis: k, lo, hi, pct: Math.round(((centre - lo) / (hi - lo)) * 1000) / 10, label: k === 1 ? 'između donje i gornje' : 'između lijeve i desne' });
    }
    return out;
  }, [moveTarget, ev, readOnly]);
  // Typed percentages belong to the selected object.
  useEffect(() => setCenterPct({}), [moveTarget?.unitId, moveTarget?.kind === 'panel' ? moveTarget.partId : null]);
  const applyCenter = (axis: 0 | 1 | 2, pct: number) => {
    const o = centerOptions.find((x) => x.axis === axis);
    const vp = vpRef.current;
    if (!o || !moveTarget || !vp) return;
    const unit = ev.cabinets.find((c) => c.instance.id === moveTarget.unitId)!;
    const part = moveTarget.kind === 'panel' ? unit.result.parts.find((p) => p.id === moveTarget.partId) : undefined;
    const me = part ? partWorldBox(unit, part) : unitWorldBox(unit);
    const target = o.lo + ((o.hi - o.lo) * pct) / 100;
    const d = Math.round(target - (me.min[axis] + me.max[axis]) / 2);
    if (d === 0) return;
    const delta: Vec3 = [0, 0, 0];
    delta[axis] = d;
    vp.onMove(moveTarget, delta);
  };

  // Two panels of one assembly meeting at a corner: swap which one runs through.
  const overlap = useMemo(() => {
    const [a, b] = linkItems;
    if (linkItems.length !== 2 || a!.kind !== 'panel' || b!.kind !== 'panel' || a!.unitId !== b!.unitId) return null;
    const asm = doc.assemblies.find((x) => x.id === a!.unitId);
    const pa = asm?.panels.find((p) => p.key === a!.panelKey);
    const pb = asm?.panels.find((p) => p.key === b!.panelKey);
    const o = asm && !asm.locked && pa && pb ? findOverlap(pa, pb) : null;
    if (!o) return null;
    const label = (k: string) => asm!.panels.find((p) => p.key === k)!.label;
    return {
      text: `prolazi: ${label(o.through)} · naslanja se: ${label(o.butting)}`,
      apply: () =>
        store.exec(
          'Zamjena preklopa',
          (d) => {
            swapOverlapInDoc(d, asm!.id, pa!.key, pb!.key);
          },
          { pins: false },
        ),
    };
  }, [linkItems, doc, store]);
  const sHeld = useRef(false);
  const [projection, setProjection] = useState<'perspective' | 'ortho'>('perspective');
  const [leftTab, setLeftTab] = useState<'scene' | 'library'>('scene');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [presetsKey, setPresetsKey] = useState(0);
  const { items: presets, error: presetsError } = usePresets(presetsKey);
  const { catalog } = useCatalog();
  const [insertError, setInsertError] = useState<string | null>(null);

  const hostRef = useRef<HTMLDivElement>(null);
  const vpRef = useRef<ThreeViewport | null>(null);

  useEffect(() => {
    const vp = new ThreeViewport(hostRef.current!);
    vpRef.current = vp;
    (window as unknown as { __scsViewport?: ThreeViewport }).__scsViewport = vp;
    return () => {
      vp.dispose();
      vpRef.current = null;
    };
  }, []);

  useEffect(() => {
    vpRef.current!.onPick = (hit, mod) => {
      if (!hit) return store.select({ cabinetIds: [], partId: null });
      const sel = store.getState().selection;
      if (mod.ctrl) {
        // Ctrl+click: add/remove a single part (e.g. several panels for linking).
        const all = [sel.partId, ...(sel.extraPartIds ?? [])].filter((x): x is string => !!x);
        const next = all.includes(hit.partId) ? all.filter((x) => x !== hit.partId) : [...all, hit.partId];
        const units = [...new Set([...sel.cabinetIds, hit.cabinetId])];
        return store.select({ cabinetIds: units, partId: next[0] ?? null, extraPartIds: next.slice(1) });
      }
      if (mod.shift) {
        const cur = sel.cabinetIds;
        return store.select({ cabinetIds: cur.includes(hit.cabinetId) ? cur.filter((c) => c !== hit.cabinetId) : [...cur, hit.cabinetId], partId: null });
      }
      store.select({ cabinetIds: [hit.cabinetId], partId: hit.partId });
    };
    vpRef.current!.onCornerState = setCornerState;
    vpRef.current!.onPinState = setPinState;
    vpRef.current!.onDuplicateDrag = setDupDrag;
    vpRef.current!.onEdgePick = setEdgePick;
  }, [store]);

  const edgeMap = useMemo(() => {
    const m = new Map<string, Record<EdgeId, string>>();
    for (const c of ev.cabinets) for (const pe of c.edges) m.set(pe.partId, Object.fromEntries(EDGE_IDS.map((e) => [e, pe.decisions[e].decision])) as Record<EdgeId, string>);
    return m;
  }, [ev]);

  const firstFrame = useRef(true);
  useEffect(() => {
    const vp = vpRef.current!;
    const setParts = selection.shelfSetId ? ev.cabinets.find((c) => c.instance.id === selection.cabinetIds[0])?.result.parts.filter((p) => p.localKey.startsWith(`${selection.shelfSetId}-`)).map((p) => p.id) ?? [] : [];
    vp.setSelection(selection.cabinetIds, selection.partId, [...(selection.extraPartIds ?? []), ...setParts]);
    vp.setDecors(decorViews(catalog));
    vp.setScene(doc, ev, edgeMap, opts);
    vp.setLinkLines(linkLines(doc, ev.cabinets), opts.links);
    // No gizmo while picking openings (it would catch the clicks).
    vp.setGizmo(gizmoOn && !readOnly && !frontPick && !regionPick && !dividerPick ? moveTarget : null);
    // Corner snap: hover a corner of the selected panel, click it, then click a corner of another panel.
    vp.setMoveTarget(readOnly ? null : moveTarget, selection.partId);
    vp.setCornersEnabled(!opts.exploded && !opts.openFronts);
    // Pin (anchor point) of the selected panel or parametric cabinet.
    const pinInfo = ((): { pin: Pin; size: Vec3 | null } | null => {
      if (readOnly || !moveTarget || opts.exploded || opts.openFronts) return null;
      if (moveTarget.kind === 'panel') {
        const asm = doc.assemblies.find((a) => a.id === moveTarget.unitId);
        const key = asm && panelKeyOfPart(asm, moveTarget.partId.split('/')[1]!);
        const panel = asm?.panels.find((x) => x.key === key);
        return panel ? { pin: panel.pin ?? DEFAULT_PIN, size: null } : null;
      }
      const cab = doc.cabinets.find((c) => c.id === moveTarget.unitId);
      const evc = ev.cabinets.find((c) => c.instance.id === moveTarget.unitId);
      return cab && evc ? { pin: cab.pin ?? DEFAULT_PIN, size: evc.result.bounds.size } : null;
    })();
    vp.setPin(pinInfo ? moveTarget : null, pinInfo?.pin ?? null, pinInfo?.size ?? null);
    // Flip orientation next to the gizmo: free panels about X/Y/Z, parametric cabinets/assemblies about Y.
    vp.setFlipAxes(!gizmoOn || readOnly || !moveTarget ? [] : moveTarget.kind === 'panel' ? ['x', 'y', 'z'] : ['y']);
    if (firstFrame.current) {
      firstFrame.current = false;
      vp.setView('persp');
    }
  }, [doc, ev, edgeMap, opts, selection, catalog, gizmoOn, moveTarget, readOnly, frontPick, regionPick, dividerPick]);

  // Snap while dragging the gizmo (S held) and committing the move as one undoable command.
  useEffect(() => {
    const vp = vpRef.current!;
    vp.onSnap = (target, raw) => {
      if (!sHeld.current) {
        setSnapInfo('');
        return { delta: raw.map((v) => Math.round(v / 1000) * 1000) as Vec3, info: '' }; // 1 mm steps
      }
      const unit = ev.cabinets.find((c) => c.instance.id === target.unitId);
      if (!unit) return { delta: raw, info: '' };
      let moving;
      const targets: SnapTarget[] = [];
      if (target.kind === 'panel') {
        const part = unit.result.parts.find((p) => p.id === target.partId)!;
        moving = partWorldBox(unit, part);
        for (const p of unit.result.parts) if (p.id !== part.id) targets.push({ box: partWorldBox(unit, p), label: p.label });
      } else {
        moving = unitWorldBox(unit);
      }
      for (const c of ev.cabinets) if (c.instance.id !== unit.instance.id && !c.instance.hidden) targets.push({ box: unitWorldBox(c), label: c.instance.name });
      const r = snapMove(moving, raw, targets, roomPlanes(doc));
      setSnapInfo(r.hits.map((h) => h.label).join(' · '));
      return { delta: r.delta, info: '' };
    };
    vp.onPinChange = (target, pin) => {
      store.exec('Pomjeranje pina', (d) => {
        if (target.kind === 'panel') {
          const asm = d.assemblies.find((a) => a.id === target.unitId);
          const key = asm && panelKeyOfPart(asm, target.partId.split('/')[1]!);
          const panel = asm?.panels.find((x) => x.key === key);
          if (panel) panel.pin = pin;
        } else {
          const cab = d.cabinets.find((c) => c.id === target.unitId);
          if (cab) cab.pin = pin;
        }
      });
    };
    vp.onFlip = (target, axis) => {
      if (target.kind === 'unit') {
        store.exec('Okretanje 90°', (d) => {
          const u = [...d.cabinets, ...d.assemblies].find((x) => x.id === target.unitId);
          if (u && !u.locked) u.transform.rotationY = ((u.transform.rotationY + 90) % 360) as typeof u.transform.rotationY;
        });
        return;
      }
      const asm = doc.assemblies.find((a) => a.id === target.unitId);
      const key = asm && panelKeyOfPart(asm, target.partId.split('/')[1]!);
      if (!asm || !key) return;
      store.exec('Okretanje ploče', (d) => {
        const a = d.assemblies.find((x) => x.id === target.unitId)!;
        if (a.locked) return;
        const i = a.panels.findIndex((x) => x.key === key);
        a.panels[i] = flipPanel(a.panels[i]!, axis);
      });
    };
    const move = (target: MoveTarget, delta: Vec3, opts?: { duplicate: boolean }) => {
      setSnapInfo('');
      const unit = ev.cabinets.find((c) => c.instance.id === target.unitId);
      if (!unit) return;
      if (opts?.duplicate) {
        // Alt + gizmo drag: the original stays, a copy lands at the new position (one undo step).
        if (target.kind === 'unit') {
          store.exec('Kopiranje', (d) => {
            const cab = d.cabinets.find((c) => c.id === target.unitId);
            const src = cab ?? d.assemblies.find((a) => a.id === target.unitId);
            if (!src) return;
            const copy = structuredClone(src);
            copy.id = newUnitId(d, cab ? 'k' : 's');
            copy.name = `${src.name} (kopija)`;
            copy.locked = false;
            copy.transform.position = [src.transform.position[0] + delta[0], src.transform.position[1] + delta[1], src.transform.position[2] + delta[2]];
            if (cab) d.cabinets.push(copy as CabinetInstance);
            else d.assemblies.push(copy as AssemblyInstance);
            queueMicrotask(() => store.select({ cabinetIds: [copy.id], partId: null }));
          });
        } else {
          const local = worldDeltaToLocal(unit, delta);
          const asm = doc.assemblies.find((a) => a.id === target.unitId);
          const key = asm && panelKeyOfPart(asm, target.partId.split('/')[1]!);
          if (!asm || !key) return;
          store.exec('Kopiranje ploče', (d) => {
            const a = d.assemblies.find((x) => x.id === target.unitId)!;
            if (a.locked) return;
            const src = a.panels.find((x) => x.key === key)!;
            const copy = structuredClone(src);
            copy.key = nextPanelKey(a);
            copy.label = `${src.label} (kopija)`;
            copy.position = [src.position[0] + local[0], src.position[1] + local[1], src.position[2] + local[2]];
            a.panels.push(copy);
            const partKey = copy.repeat && copy.repeat.count > 1 ? `${copy.key}-1` : copy.key;
            queueMicrotask(() => store.select({ cabinetIds: [a.id], partId: `${a.id}/${partKey}` }));
          });
        }
        return;
      }
      if (target.kind === 'unit') {
        store.exec('Pomjeranje', (d) => {
          const u = [...d.cabinets, ...d.assemblies].find((x) => x.id === target.unitId);
          if (!u || u.locked) return;
          u.transform.position = [u.transform.position[0] + delta[0], u.transform.position[1] + delta[1], u.transform.position[2] + delta[2]];
        });
      } else {
        const local = worldDeltaToLocal(unit, delta);
        const asm = doc.assemblies.find((a) => a.id === target.unitId);
        const key = asm && panelKeyOfPart(asm, target.partId.split('/')[1]!);
        if (!asm || !key) return;
        store.exec('Pomjeranje ploče', (d) => {
          const a = d.assemblies.find((x) => x.id === target.unitId)!;
          if (a.locked) return;
          const pnl = a.panels.find((x) => x.key === key)!;
          pnl.position = [pnl.position[0] + local[0], pnl.position[1] + local[1], pnl.position[2] + local[2]];
        });
      }
    };
    vp.onMove = (target, delta, opts) => {
      move(target, delta, opts);
      lastMove.current = opts?.duplicate ? null : (store.getState().past.at(-1) ?? null);
    };
    // Exact distance typed after a drag: redo that drag with the typed delta (one undo step), or
    // move by the difference if something else happened in between.
    vp.onExactMove = (target, from, to) => {
      const st = store.getState();
      const last = st.past.at(-1);
      if (last && last === lastMove.current && st.doc === last.after) {
        store.undo();
        move(target, to);
      } else {
        move(target, [to[0] - from[0], to[1] - from[1], to[2] - from[2]]);
      }
      lastMove.current = store.getState().past.at(-1) ?? null;
    };
  }, [ev, doc, store]);

  // Warn before closing with unsaved server state (the journal still keeps a local copy).
  useEffect(() => {
    const on = (e: BeforeUnloadEvent) => {
      if (state.doc !== state.serverDoc) e.preventDefault();
    };
    window.addEventListener('beforeunload', on);
    return () => window.removeEventListener('beforeunload', on);
  }, [state.doc, state.serverDoc]);

  const selCab = selection.cabinetIds.length === 1 ? doc.cabinets.find((c) => c.id === selection.cabinetIds[0]) : undefined;
  const selAsm = selection.cabinetIds.length === 1 ? doc.assemblies.find((a) => a.id === selection.cabinetIds[0]) : undefined;
  const selUnit = selCab ?? selAsm;
  const selEval = selUnit ? ev.cabinets.find((c) => c.instance.id === selUnit.id) : undefined;
  const selPart = selEval && selection.partId ? selEval.result.parts.find((p) => p.id === selection.partId) : undefined;
  const selFront = ((): { asm: NonNullable<typeof selAsm>; front: NonNullable<ReturnType<typeof frontOfPart>> } | null => {
    const f = selAsm && selPart ? frontOfPart(selAsm, selPart.localKey) : null;
    return f && selAsm ? { asm: selAsm, front: f } : null;
  })();
  /** Selected shelf set: a single shelf picked in the scene, or the set row in the outliner. */
  const selShelf = ((): { set: ShelfSet; index: number | null } | null => {
    if (!selAsm) return null;
    const hit = selPart ? shelfOfPart(selAsm, selPart.localKey) : null;
    if (hit) return { set: hit.set, index: hit.index };
    const set = selection.shelfSetId ? selAsm.shelfSets?.find((x) => x.id === selection.shelfSetId) : undefined;
    return set ? { set, index: null } : null;
  })();

  const actions = {
    duplicate: () => {
      if (!selection.cabinetIds.length) return;
      store.exec('Dupliranje', (d) => {
        const ids: string[] = [];
        for (const id of selection.cabinetIds) {
          const evSrc = ev.cabinets.find((c) => c.instance.id === id);
          const cab = d.cabinets.find((c) => c.id === id);
          const asm = d.assemblies.find((a) => a.id === id);
          const src = cab ?? asm;
          if (!src || !evSrc) continue;
          const copy = structuredClone(src);
          copy.id = newUnitId(d, cab ? 'k' : 's');
          copy.name = `${src.name} (kopija)`;
          copy.transform.position[0] += evSrc.result.bounds.size[0];
          if (cab) d.cabinets.push(copy as CabinetInstance);
          else d.assemblies.push(copy as AssemblyInstance);
          ids.push(copy.id);
        }
        queueMicrotask(() => store.select({ cabinetIds: ids, partId: null }));
      });
    },
    remove: () => {
      if (!selection.cabinetIds.length) return;
      store.exec('Brisanje', (d) => {
        d.cabinets = d.cabinets.filter((c) => !selection.cabinetIds.includes(c.id) || c.locked);
        d.assemblies = d.assemblies.filter((a) => !selection.cabinetIds.includes(a.id) || a.locked);
      });
    },
    mirror: () =>
      store.exec('Ogledalo', (d) => {
        for (const c of allUnits(d)) if (selection.cabinetIds.includes(c.id) && !c.locked) c.transform.mirrored = !c.transform.mirrored;
      }),
    rotate: () =>
      store.exec('Rotacija 90°', (d) => {
        for (const c of allUnits(d)) if (selection.cabinetIds.includes(c.id) && !c.locked) c.transform.rotationY = (((c.transform.rotationY + 90) % 360) as 0 | 90 | 180 | 270);
      }),
    toggle: (key: 'hidden' | 'locked') =>
      store.exec(key === 'hidden' ? 'Sakrij/prikaži' : 'Zaključaj/otključaj', (d) => {
        for (const c of allUnits(d)) if (selection.cabinetIds.includes(c.id)) c[key] = !c[key];
      }),
    addPanel: () => {
      const target = selAsm;
      if (target) {
        const used = new Set(target.panels.map((p) => p.key));
        let n = target.panels.length + 1;
        while (used.has(`ploca-${n}`)) n++;
        const key = `ploca-${n}`;
        // Place the new panel on top of the existing ones so it never starts inside another panel.
        const top = ev.cabinets.find((c) => c.instance.id === target.id)?.result.bounds.size[1] ?? 0;
        store.exec('Dodaj ploču', (d) => {
          d.assemblies.find((a) => a.id === target.id)!.panels.push({ ...defaultPanel(key), label: `Ploča ${n}`, position: [0, top, 0] });
        });
        store.select({ cabinetIds: [target.id], partId: `${target.id}/${key}` });
        return;
      }
      let id = '';
      store.exec('Novi sklop od ploča', (d) => {
        id = newUnitId(d, 's');
        const maxX = ev.cabinets.reduce((m, c) => Math.max(m, c.instance.transform.position[0] + c.result.bounds.size[0]), 0);
        d.assemblies.push({
          id,
          name: `${id.toUpperCase()} Sklop`,
          panels: [{ ...defaultPanel('ploca-1'), label: 'Ploča 1' }],
          transform: { position: [maxX, 0, 0], rotationY: 0, mirrored: false },
          materials: {},
          edgeOverrides: {},
          hidden: false,
          locked: false,
          environment: 'dry' as const,
          preset: null,
        });
      });
      store.select({ cabinetIds: [id], partId: `${id}/ploca-1` });
    },
    insertPreset: async (p: PresetSummaryDto) => {
      setInsertError(null);
      try {
        const content = await loadVersion(p.latestVersionId);
        const r = instantiatePreset(store.getState().doc, p, p.latestVersionId, p.latestVersion, content, ev.cabinets);
        store.exec(`Ubaci preset: ${p.name}`, (d) => {
          if (r.kind === 'cabinet') d.cabinets.push(r.unit);
          else d.assemblies.push(r.unit);
        });
        store.select({ cabinetIds: [r.unit.id], partId: null });
      } catch (e) {
        setInsertError((e as Error).message);
      }
    },
    nudge: (dx: number, dz: number) =>
      store.exec('Pomjeranje', (d) => {
        for (const c of allUnits(d)) {
          if (!selection.cabinetIds.includes(c.id) || c.locked) continue;
          c.transform.position[0] += dx;
          c.transform.position[2] += dz;
        }
      }),
  };

  // Keyboard shortcuts
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('input, select, textarea')) return;
      if (e.key === 's' || e.key === 'S') {
        if (!(e.ctrlKey || e.metaKey)) {
          sHeld.current = true;
          return;
        }
      }
      if (e.key === 'Escape' && vpRef.current?.isRegionPicking()) return (cancelRegionPick(), cancelFrontPick(), cancelDivider());
      if (e.key === 'Escape' && vpRef.current?.isPinMoving()) return vpRef.current.cancelPinMove();
      if (e.key === 'Escape' && vpRef.current?.hasEdgeSelection()) return vpRef.current.clearEdges();
      if (e.key === 'Escape' && vpRef.current?.hasCornerSelection()) return vpRef.current.clearCorner();
      if ((e.key === 'l' || e.key === 'L') && canEdit && linkCandidates(doc, selection).length >= 2) return setLinkOpen(true);
      if (e.key === 'g' || e.key === 'G') return setGizmoOn((g) => !g);
      const mod = e.ctrlKey || e.metaKey;
      const step = mm(e.shiftKey ? 100 : 10);
      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) (e.preventDefault(), store.undo());
      else if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) (e.preventDefault(), store.redo());
      else if (mod && e.key.toLowerCase() === 's') (e.preventDefault(), void store.saveNow());
      else if (mod && e.key.toLowerCase() === 'd') (e.preventDefault(), actions.duplicate());
      else if (e.key === 'Delete') actions.remove();
      else if (e.key === 'f' || e.key === 'F') vpRef.current?.frameSelection();
      else if (e.key === '1') vpRef.current?.setView('front');
      else if (e.key === '3') vpRef.current?.setView('right');
      else if (e.key === '7') vpRef.current?.setView('top');
      else if (e.key === '0') vpRef.current?.setView('persp');
      else if (e.key === 'x' || e.key === 'X') setOpts((o) => ({ ...o, xray: !o.xray }));
      else if (e.key === 'e' || e.key === 'E') setOpts((o) => ({ ...o, edges: !o.edges }));
      else if (e.key === 'Escape') store.select({ cabinetIds: [], partId: null });
      else if (e.key.startsWith('Arrow') && selection.cabinetIds.length) {
        e.preventDefault();
        const map: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
        const [dx, dz] = map[e.key]!;
        actions.nudge(dx, dz);
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === 's' || e.key === 'S') {
        sHeld.current = false;
        setSnapInfo('');
      }
    };
    const blur = () => (sHeld.current = false);
    window.addEventListener('keydown', on);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', on);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  });

  const toggleOpt = (k: keyof ViewOptions, label: string, hint?: string) => (
    <button className={`btn sm ${opts[k] ? 'on' : ''}`} aria-pressed={opts[k]} title={hint ?? label} onClick={() => setOpts((o) => ({ ...o, [k]: !o[k] }))}>
      {label}
    </button>
  );
  const view = (v: ViewName, label: string, key?: string) => (
    <button className="btn sm ghost" title={key ? `${label} (${key})` : label} onClick={() => vpRef.current?.setView(v)}>
      {label}
    </button>
  );
  const hasSel = selection.cabinetIds.length > 0;
  const canEdit = !readOnly;

  return (
    <CatalogContext.Provider value={catalog}>
    <div className="editor">
      <header className="topbar">
        <button className="btn ghost sm" onClick={() => navigate('/')} title={t('editor.backToDash')}>
          <Logo size={18} /> ←
        </button>
        <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loaded.project.name}</strong>
        {loaded.project.isDemo && <span className="badge demo">DEMO</span>}
        <span className="faint">rev {state.baseRevision}</span>
        <div className={`save-state ${state.save}`} role="status" aria-live="polite" title={state.saveError ?? undefined}>
          <span className="dot" />
          {t(SAVE_LABEL[state.save])}
        </div>
        <div className="spacer" />
        <TopbarUser />
      </header>
      <div className="toolbar" role="toolbar">
        <button className="btn sm primary" disabled={!canEdit || state.doc === state.serverDoc || state.save === 'saving'} onClick={() => void store.saveNow()} title="Ctrl+S">
          {t('save.save')}
        </button>
        <button className="btn sm icon" disabled={!state.past.length} onClick={() => store.undo()} title={`${t('editor.undo')} (Ctrl+Z)${state.past.at(-1) ? `: ${state.past.at(-1)!.label}` : ''}`} aria-label={t('editor.undo')}>
          ↶
        </button>
        <button className="btn sm icon" disabled={!state.future.length} onClick={() => store.redo()} title={`${t('editor.redo')} (Ctrl+Y)${state.future[0] ? `: ${state.future[0].label}` : ''}`} aria-label={t('editor.redo')}>
          ↷
        </button>
        <span className="sep" />
        <button className={`btn sm ${leftTab === 'library' ? 'on' : ''}`} onClick={() => setLeftTab(leftTab === 'library' ? 'scene' : 'library')} title="Biblioteka preseta">
          + {t('editor.add')}
        </button>
        <button className="btn sm ghost" disabled={!canEdit} onClick={actions.addPanel} title="Nova ploča (u selektovani sklop ili novi sklop)">
          + Ploča
        </button>
        <button
          className={`btn sm ghost ${dividerPick ? 'on' : ''}`}
          disabled={!canEdit || !frontUnit || frontUnit.locked}
          onClick={() => (dividerPick ? cancelDivider() : startDivider(false))}
          title={frontUnit ? 'Presijeci prostor novom pločom (vertikalno ili horizontalno)' : 'Izaberite sklop od ploča'}
        >
          + Pregrada
        </button>
        <button className={`btn sm ghost ${frontPick ? 'on' : ''}`} disabled={!canEdit || !frontUnit || frontUnit.locked} onClick={() => (frontPick ? cancelFrontPick() : startFront(false))} title={frontUnit ? 'Fronta preko jednog ili više prostora (Ctrl+klik spaja prostore)' : 'Izaberite sklop od ploča'}>
          + Fronta
        </button>
        <button
          className="btn sm ghost"
          disabled={!canEdit || !shelfCandidate || 'error' in shelfCandidate}
          onClick={startShelves}
          title={shelfCandidate && 'error' in shelfCandidate ? shelfCandidate.error : 'Police između dvije horizontalne ploče: izaberite donju i gornju (Ctrl+klik)'}
        >
          + Police
        </button>
        <button className="btn sm ghost" disabled={!canEdit || !hasSel} onClick={() => setWizardOpen(true)} title="Kreiraj preset iz selekcije">
          ★ Preset iz selekcije
        </button>
        <button className="btn sm ghost" disabled={!canEdit || !hasSel} onClick={actions.duplicate} title={`${t('editor.duplicate')} (Ctrl+D)`}>
          {t('editor.duplicate')}
        </button>
        <button className="btn sm ghost" disabled={!canEdit || !hasSel} onClick={actions.mirror}>
          {t('editor.mirror')}
        </button>
        <button className="btn sm ghost" disabled={!canEdit || !hasSel} onClick={actions.rotate} title="Rotacija 90°">
          ⟳ 90°
        </button>
        <button className="btn sm ghost" disabled={!canEdit || !hasSel} onClick={() => actions.toggle('hidden')}>
          {t('editor.hide')}
        </button>
        <button className="btn sm ghost" disabled={!canEdit || !hasSel} onClick={() => actions.toggle('locked')}>
          {t('editor.lock')}
        </button>
        <button className="btn sm ghost danger" disabled={!canEdit || !hasSel} onClick={actions.remove} title={`${t('editor.delete')} (Del)`}>
          {t('editor.delete')}
        </button>
        <span className="sep" />
        <button
          className="btn sm"
          onClick={() => {
            const next = projection === 'perspective' ? 'ortho' : 'perspective';
            setProjection(next);
            vpRef.current?.setProjection(next);
          }}
        >
          {projection === 'perspective' ? t('editor.perspective') : t('editor.ortho')}
        </button>
        {view('persp', '3D', '0')}
        {view('front', t('editor.front'), '1')}
        {view('back', t('editor.back'))}
        {view('left', t('editor.left'))}
        {view('right', t('editor.right'), '3')}
        {view('top', t('editor.top'), '7')}
        <button className="btn sm ghost" onClick={() => vpRef.current?.frameSelection()} title={`${t('editor.frame')} (F)`}>
          ⌖
        </button>
        <span className="sep" />
        {toggleOpt('grid', t('editor.grid'))}
        {toggleOpt('xray', t('editor.xray'), 'X-ray (X)')}
        {toggleOpt('exploded', t('editor.exploded'))}
        {toggleOpt('labels', t('editor.labels'))}
        {toggleOpt('edges', t('editor.edgeView'), 'Prikaz kanta (E)')}
        {toggleOpt('openFronts', t('editor.openFronts'), 'Pregled otvaranja — ne mijenja proizvodnu geometriju')}
        <span className="sep" />
        <button className={`btn sm ${gizmoOn ? 'on' : ''}`} aria-pressed={gizmoOn} onClick={() => setGizmoOn(!gizmoOn)} title="Gizmo za pomjeranje (G). Držite S za snap.">
          ✥ Pomjeri
        </button>
        <button className="btn sm" disabled={!canEdit || linkCandidates(doc, selection).length < 2} onClick={() => setLinkOpen(true)} title="Linkuj mjere (L). Ploče birajte Ctrl+klikom, elemente Shift+klikom.">
          🔗 Link
        </button>
        {toggleOpt('links', 'Linkovi', 'Prikaz linkova (isprekidane linije)')}
      </div>
      <div className="workspace">
        <aside className="panel panel-left" aria-label={t('editor.scene')}>
          <div className="panel-tabs" role="tablist">
            <button className={leftTab === 'scene' ? 'on' : ''} onClick={() => setLeftTab('scene')}>
              {t('editor.scene')} ({doc.cabinets.length + doc.assemblies.length})
            </button>
            <button className={leftTab === 'library' ? 'on' : ''} onClick={() => setLeftTab('library')}>
              Katalog
            </button>
          </div>
          {leftTab === 'library' ? (
            <>
              {insertError && <p className="error-text" style={{ padding: '0 8px' }}>{insertError}</p>}
              <LibraryPanel presets={presets} error={presetsError} disabled={!canEdit} onInsert={(p) => void actions.insertPreset(p)} />
            </>
          ) : (
            <SceneTree doc={doc} ev={ev} selection={selection} store={store} canEdit={canEdit} />
          )}
        </aside>
        <div className="viewport" ref={hostRef}>
          <div className="vp-actions" hidden={linkOpen}>
            {connected && (
              <div className="vp-card vp-card-ask" key={`c-${state.past.length}`} role="group" aria-label="Ažuriranje spojenih ploča">
                <div className="vp-card-text">
                  <strong>Pomjeriti spojene ploče?</strong>
                  <span title={connected.map((m) => m.label).join(', ')}>
                    {connected.length === 1 ? connected[0]!.label : `${connected.length} ploče`} — prate novu mjeru
                  </span>
                </div>
                <button className="btn sm primary" onClick={() => store.exec('Ažuriranje spojenih', (d) => applyConnectedMoves(d, connected))}>
                  Update connected
                </button>
              </div>
            )}
            {dividerPick && (
              <div className="vp-card vp-card-copy" role="group" aria-label="Nova pregrada">
                <div className="vp-card-text">
                  <strong>{!dividerPick.spaces.length ? 'Nema otvorenog prostora' : dividerPick.selected === null ? 'Pregrada: izaberite prostor' : 'Kako presjeći prostor?'}</strong>
                  <span>
                    {dividerPick.selected === null
                      ? 'klik na prostor koji presijecate'
                      : `${((dividerPick.spaces[dividerPick.selected]!.rect.x1 - dividerPick.spaces[dividerPick.selected]!.rect.x0) / 1000).toFixed(1)} × ${((dividerPick.spaces[dividerPick.selected]!.rect.y1 - dividerPick.spaces[dividerPick.selected]!.rect.y0) / 1000).toFixed(1)} mm · ploča ide na sredinu`}
                  </span>
                </div>
                {(doc.assemblies.find((a) => a.id === dividerPick.unitId)?.shelfSets ?? []).length > 0 && dividerPick.selected === null && (
                  <div className="seg" role="group" aria-label="Podjela prostora">
                    <button className={`btn sm ${dividerPick.betweenShelves ? 'ghost' : 'on'}`} onClick={() => dividerPick.betweenShelves && startDivider(false)}>
                      Cijeli prostori
                    </button>
                    <button className={`btn sm ${dividerPick.betweenShelves ? 'on' : 'ghost'}`} onClick={() => !dividerPick.betweenShelves && startDivider(true)}>
                      Između polica
                    </button>
                  </div>
                )}
                {dividerPick.selected !== null && (
                  <>
                    <button className="btn sm primary" onClick={() => createDivider('vertical')} title="Uspravna pregrada (dijeli lijevo / desno)">
                      ┃ Vertikalno
                    </button>
                    <button className="btn sm primary" onClick={() => createDivider('horizontal')} title="Vodoravna ploča (dijeli gore / dolje)">
                      ━ Horizontalno
                    </button>
                  </>
                )}
                <button className="btn sm ghost" onClick={cancelDivider}>
                  Otkaži
                </button>
              </div>
            )}
            {frontPick && (
              <div className={`vp-card ${frontPreview?.irregular ? 'vp-card-ask' : 'vp-card-copy'}`} role="group" aria-label="Nova fronta">
                <div className="vp-card-text">
                  <strong>{frontPick.spaces.length ? `Fronta: ${frontPick.selected.length ? `${frontPick.selected.length} ${frontPick.selected.length === 1 ? 'prostor' : 'prostora'}` : 'izaberite prostor'}` : 'Nema otvorenog prostora'}</strong>
                  <span className={frontPreview?.irregular ? 'warn-line' : undefined}>
                    {frontPreview?.irregular
                      ? 'Nepravilan oblik — ide na CNC obradu, mjere posebno u tabeli'
                      : frontPreview
                        ? `${((frontPreview.bbox.x1 - frontPreview.bbox.x0) / 1000).toFixed(1)} × ${((frontPreview.bbox.y1 - frontPreview.bbox.y0) / 1000).toFixed(1)} mm · Ctrl+klik dodaje`
                        : 'klik bira, Ctrl+klik dodaje prostore'}
                  </span>
                </div>
                {(doc.assemblies.find((a) => a.id === frontPick.unitId)?.shelfSets ?? []).length > 0 && (
                  <div className="seg" role="group" aria-label="Podjela prostora">
                    <button className={`btn sm ${frontPick.betweenShelves ? 'ghost' : 'on'}`} onClick={() => frontPick.betweenShelves && startFront(false)} title="Prostori između stranica i pregrada (police se ne računaju)">
                      Cijeli prostori
                    </button>
                    <button className={`btn sm ${frontPick.betweenShelves ? 'on' : 'ghost'}`} onClick={() => !frontPick.betweenShelves && startFront(true)} title="Svaki razmak između polica je poseban prostor">
                      Između polica
                    </button>
                  </div>
                )}
                <button className="btn sm primary" disabled={!frontPick.selected.length} onClick={createFront}>
                  Kreiraj
                </button>
                <button className="btn sm ghost" onClick={cancelFrontPick}>
                  Otkaži
                </button>
              </div>
            )}
            {selFront && selFront.front.hinge !== null && !frontPick && !dividerPick && !selFront.asm.locked && (
              <div className="vp-card vp-card-link" key={`split-${selFront.front.id}`} role="group" aria-label="Podjela fronte">
                <div className="vp-card-text">
                  <strong>Podijeli frontu</strong>
                  <span>{selFront.front.label}</span>
                </div>
                <button className="btn sm" onClick={() => doSplitFront(selFront.asm.id, selFront.front.id, 'vertical')} title="Lijeva i desna fronta, fuga između">
                  ┃ Lijevo / desno
                </button>
                <button className="btn sm" onClick={() => doSplitFront(selFront.asm.id, selFront.front.id, 'horizontal')} title="Donja i gornja fronta, fuga između">
                  ━ Gore / dolje
                </button>
              </div>
            )}
            {shelfFit && !frontPick && !dividerPick && (
              <div className="vp-card vp-card-ask" key={`fit-${shelfFit.hits.length}`} role="group" aria-label="Prilagođavanje polica">
                <div className="vp-card-text">
                  <strong>Unutarnja fronta udara u police ({shelfFit.hits.length})</strong>
                  <span>Automatski prilagoditi dužinu polica?</span>
                </div>
                <button className="btn sm primary" onClick={fitShelves} title="Skraćuje samo police koje se sada sudaraju s frontom, do njene zadnje strane">
                  Da, skrati police
                </button>
                <button className="btn sm ghost" onClick={() => setShelfFitDismissed(doc)}>
                  Ne
                </button>
              </div>
            )}
            {selFront && selFront.front.hinge === null && !frontPick && (
              <div className="vp-card vp-card-ask" key={`h-${selFront.front.id}`} role="group" aria-label="Strana šarki">
                <div className="vp-card-text">
                  <strong>Šarke: na koju stranu?</strong>
                  <span>{selFront.front.label}</span>
                </div>
                {HINGES.map((h) => (
                  <button
                    key={h}
                    className={`btn sm ${h === 'none' ? 'ghost' : ''}`}
                    onClick={() =>
                      store.exec('Šarke fronte', (d) => {
                        const f = d.assemblies.find((a) => a.id === selFront.asm.id)?.fronts?.find((x) => x.id === selFront.front.id);
                        if (f) f.hinge = h;
                      })
                    }
                  >
                    {HINGE_LABEL[h]}
                  </button>
                ))}
              </div>
            )}
            {centerOptions.length > 0 && !regionPick && !frontPick && !dividerPick && (
              <div className="vp-card vp-card-center" key={`c-${moveTarget?.kind === 'panel' ? moveTarget.partId : moveTarget?.unitId}`} role="group" aria-label="Centriranje">
                <div className="vp-card-text">
                  <strong>Centriranje</strong>
                  <span>sredina ploče u prostoru</span>
                </div>
                <div className="center-rows">
                  {centerOptions.map((o) => {
                    const AX = ['X', 'Y', 'Z'][o.axis];
                    const val = centerPct[o.axis] ?? '50';
                    const pct = Number(val.replace(',', '.'));
                    const ok = Number.isFinite(pct) && pct >= 0 && pct <= 100;
                    return (
                      <div key={o.axis} className="center-row" title={`${o.label} · sada ${o.pct}%`}>
                        <span className={`ax ax-${o.axis}`}>{AX}</span>
                        <input
                          className={`input${ok ? '' : ' invalid'}`}
                          value={val}
                          aria-label={`Postotak ${AX}`}
                          onChange={(e) => setCenterPct({ ...centerPct, [o.axis]: e.target.value })}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter' && ok) applyCenter(o.axis, pct);
                          }}
                        />
                        <span className="faint">%</span>
                        <button className="btn sm" disabled={!ok} onClick={() => applyCenter(o.axis, pct)}>
                          {pct === 50 ? 'Center' : 'Pomjeri'}
                        </button>
                        <span className="faint now">sada {o.pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {copyDim && (
              <div className="vp-card vp-card-copy" key={`e-${JSON.stringify(edgePick.target)}`} role="group" aria-label="Kopiraj dimenziju">
                <div className="vp-card-text">
                  <strong>{formatMm(copyDim.value)} mm → {copyDim.label}</strong>
                  <span title={copyDim.blocked ?? undefined}>{copyDim.blocked ?? `${copyDim.prop} sada ${formatMm(copyDim.current)} mm`}</span>
                </div>
                <button className="btn sm primary" disabled={!!copyDim.blocked || copyDim.value === copyDim.current} onClick={copyDim.apply}>
                  Kopiraj dimenziju
                </button>
              </div>
            )}
            {linkItems.length >= 2 && (
              <div className="vp-card vp-card-link" role="group" aria-label="Link">
                <div className="vp-card-text">
                  <strong>{linkItems.length} izabrano</strong>
                  <span>{overlap ? overlap.text : shelfCandidate && !('error' in shelfCandidate) ? (shelfCandidate.b.mismatch ? 'gornja i donja nisu istih mjera' : 'police između ove dvije ploče') : 'poveži mjere'}</span>
                </div>
                {shelfItems ? (
                  <>
                    <button className="btn sm" onClick={() => linkEqual(store.exec.bind(store), shelfItems, ['thickness'], 'Link debljina polica')}>
                      Link debljina
                    </button>
                    <button className="btn sm" onClick={() => linkEqual(store.exec.bind(store), shelfItems, ['gap'], 'Link zazor polica')}>
                      Link zazor
                    </button>
                    <button className="btn sm" onClick={() => linkEqual(store.exec.bind(store), shelfItems, ['count'], 'Link broj polica')}>
                      Link broj
                    </button>
                    <button className="btn sm primary" onClick={() => linkEqual(store.exec.bind(store), shelfItems, SHELF_PROPS, 'Link police (sve)')} title="Broj polica, zazor i debljina">
                      Link all
                    </button>
                  </>
                ) : (
                  <button className="btn sm" onClick={() => setLinkOpen(true)} title="Linkuj mjere (L)">
                    🔗 Link
                  </button>
                )}
                {shelfCandidate && !('error' in shelfCandidate) && (
                  <button className="btn sm" onClick={startShelves} title="Napravi police između izabranih ploča">
                    ▤ + Police
                  </button>
                )}
                {overlap && (
                  <button className="btn sm" onClick={overlap.apply} title="Zamijeni koja ploča prelazi preko koje na ovom spoju">
                    ⇄ Zamijeni preklop
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="viewport-overlay">
            {opts.edges && (
              <div className="legend">
                <span>
                  <i style={{ background: 'var(--edge-band)' }} />
                  kant
                </span>
                <span>
                  <i style={{ background: 'var(--edge-unresolved)' }} />
                  neriješeno
                </span>
                <span>
                  <i style={{ background: 'var(--info)' }} />
                  zaptivanje
                </span>
                <span>
                  <i style={{ background: 'var(--ok)' }} />
                  fabrička
                </span>
              </div>
            )}
            {opts.openFronts && <span className="legend">Pregled otvaranja (ne mijenja proizvodnu geometriju)</span>}
            {snapInfo && <span className="legend">Snap · {snapInfo}</span>}
            {cornerState === 'selected' && <span className="legend">Ugao izabran — kliknite ugao druge ploče da se poklope (Esc prekida)</span>}
            {dividerPick && dividerPick.spaces.length > 0 && <span className="legend vp-ask">Pregrada: kliknite prostor, pa izaberite smjer presjeka (Esc prekida)</span>}
            {frontPick && frontPick.spaces.length > 0 && <span className="legend vp-ask">Izaberite prostor za frontu — klik bira, Ctrl+klik dodaje prostore u jednu frontu (Esc prekida)</span>}
            {regionPick && <span className="legend vp-ask">Izaberi prostor za police — kliknite jedan od zelenih prostora (Esc prekida)</span>}
            {edgePick.source && !edgePick.target && <span className="legend">Ivica izabrana — kliknite ivicu bilo koje ploče (Esc prekida)</span>}
            {dupDrag && <span className="legend">Alt · kopija — original ostaje na mjestu</span>}
            {pinState === 'moving' && <span className="legend">Pin: približite ugao ili sredinu ivice — klik potvrđuje, Esc poništava</span>}
            {opts.links && doc.links.length > 0 && (
              <span className="legend">
                <span>
                  <i className="link-swatch equal" /> link
                </span>
                <span>
                  <i className="link-swatch offset" /> link sa razlikom
                </span>
              </span>
            )}
          </div>
        </div>
        <aside className="panel panel-right" aria-label={t('editor.properties')}>
          <div className="panel-head">
            <h3>{t('editor.properties')}</h3>
          </div>
          {selPart && selEval && <PartProperties part={selPart} cab={selEval} exec={store.exec.bind(store)} disabled={!canEdit || selUnit!.locked} />}
          {selUnit && (
            <LinksSection
              doc={doc}
              unitId={selUnit.id}
              panelKey={selAsm && selPart ? panelKeyOfPart(selAsm, selPart.localKey) : undefined}
              setId={selShelf?.set.id}
              exec={store.exec.bind(store)}
              disabled={!canEdit}
            />
          )}
          {selAsm && selection.frontGroup && !selPart && <FrontGroupProperties inst={selAsm} exec={store.exec.bind(store)} disabled={!canEdit} />}
          {selFront && <FrontProperties key={selFront.front.id} inst={selFront.asm} front={selFront.front} exec={store.exec.bind(store)} disabled={!canEdit} onRemoved={() => store.select({ cabinetIds: [selFront.asm.id], partId: null })} onSplit={(dir) => doSplitFront(selFront.asm.id, selFront.front.id, dir)} />}
          {selAsm && selShelf && (
            <ShelfProperties
              key={selShelf.set.id}
              inst={selAsm}
              set={selShelf.set}
              shelfIndex={selShelf.index}
              exec={store.exec.bind(store)}
              disabled={!canEdit}
              onSelectSet={() => store.select({ cabinetIds: [selAsm.id], partId: null, shelfSetId: selShelf.set.id })}
              onRemoved={() => store.select({ cabinetIds: [selAsm.id], partId: null })}
            />
          )}
          {selCab ? (
            <>
              {selCab.preset && <PresetSection key={`${selCab.id}:${selCab.preset.versionId}`} inst={selCab} exec={store.exec.bind(store)} disabled={!canEdit || selCab.locked} presets={presets} />}
              <CabinetProperties key={selCab.id} inst={selCab} exec={store.exec.bind(store)} disabled={!canEdit} />
            </>
          ) : selAsm ? (
            <AssemblyProperties
              key={selAsm.id}
              inst={selAsm}
              partLocalKey={selPart?.localKey ?? null}
              exec={store.exec.bind(store)}
              disabled={!canEdit}
              onSelectPanel={(k) => store.select({ cabinetIds: [selAsm.id], partId: k ? `${selAsm.id}/${k}` : null })}
            />
          ) : selection.cabinetIds.length > 1 ? (
            <p className="muted" style={{ padding: 12 }}>
              Selektovano {selection.cabinetIds.length} elemenata. Strelice pomjeraju (Shift = 100 mm). „Preset iz selekcije” pravi statički sklop.
            </p>
          ) : (
            <>
              <p className="muted" style={{ padding: '12px 12px 0' }}>{t('editor.noSelection')}</p>
              <ProjectProperties doc={doc} exec={store.exec.bind(store)} disabled={!canEdit} />
            </>
          )}
        </aside>
        <BottomPanel
          doc={doc}
          ev={ev}
          projectId={loaded.project.id}
          savedRevision={state.baseRevision}
          dirty={state.doc !== state.serverDoc}
          onSelect={(cabinetId, partId) => store.select({ cabinetIds: [cabinetId], partId })}
        />
      </div>

      {linkOpen && <LinkDialog doc={doc} items={linkCandidates(doc, selection)} exec={store.exec.bind(store)} onClose={() => setLinkOpen(false)} />}

      {wizardOpen && (
        <PresetWizard
          units={ev.cabinets.filter((c) => selection.cabinetIds.includes(c.instance.id))}
          exec={store.exec.bind(store)}
          snapshot={() => vpRef.current?.snapshot() ?? Promise.resolve(null)}
          onClose={() => setWizardOpen(false)}
          onSaved={() => {
            setWizardOpen(false);
            setPresetsKey((k) => k + 1);
            setLeftTab('library');
          }}
        />
      )}

      {journal && (
        <div className="modal-backdrop">
          <div className="card modal" role="dialog" aria-modal>
            <h2>{t('editor.recoverTitle')}</h2>
            <p className="muted">
              Lokalna kopija od {new Date(journal.updatedAt).toLocaleString()} (bazirana na reviziji {journal.baseRevision}; server je na reviziji {loaded.revision}).
              {journal.baseRevision !== loaded.revision && ' Server je u međuvremenu izmijenjen — vraćanje će napraviti izmjenu preko novije revizije.'}
            </p>
            <div className="actions">
              <button
                className="btn"
                onClick={async () => {
                  await clearJournal(userId, loaded.project.id);
                  setJournal(null);
                }}
              >
                {t('editor.recoverDiscard')}
              </button>
              {journal.baseRevision !== loaded.revision && (
                <button
                  className="btn"
                  onClick={async () => {
                    const p = await Api.createProject(`${loaded.project.name} (lokalna kopija)`.slice(0, 200), loaded.project.client, journal.document);
                    await clearJournal(userId, loaded.project.id);
                    navigate(`/p/${p.id}`);
                  }}
                >
                  Snimi kao novi projekat
                </button>
              )}
              <button
                className="btn primary"
                disabled={readOnly}
                onClick={() => {
                  store.restoreFromJournal(migrateDocument(journal.document));
                  setJournal(null);
                }}
              >
                {t('editor.recoverApply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {state.save === 'conflict' && (
        <div className="modal-backdrop">
          <div className="card modal" role="alertdialog" aria-modal>
            <h2>{t('editor.conflictTitle')}</h2>
            <p className="muted">
              {t('editor.conflictText')} (vaša baza: rev {state.baseRevision}, server: rev {state.conflict?.headRevision})
            </p>
            <div className="actions" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <button className="btn" onClick={() => void store.loadServerVersion()}>
                {t('editor.conflictLoad')}
              </button>
              <button
                className="btn primary"
                onClick={async () => {
                  const id = await store.saveAsCopy(`${loaded.project.name} (moja verzija)`.slice(0, 200));
                  await clearJournal(userId, loaded.project.id);
                  navigate(`/p/${id}`);
                }}
              >
                {t('editor.conflictCopy')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </CatalogContext.Provider>
  );
}
